import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, errorCode, errorMessage } from './api.ts';
import type { ApiErrorBody, SlideFilePayload, SlideWritePayload } from '../../shared/api.ts';
import type { EditableField, ListGroup, MediaField, SlideAccent, SlideLayout, SlideOption } from '../../shared/deck.ts';

/** One pending text edit — the field's id and its new value. */
export type FieldEdit = { id: string; value: string };

/** A 409 the panel has to explain. `retry` is the edit to re-apply, and is
 *  null for a 'preview' conflict: the proposal would just re-block the next
 *  write, so overwriting loops. See discardPreview. */
export type SlideConflict = {
  message: string;
  code: ApiErrorBody['code'] | null;
  retry: FieldEdit | null;
};

type SlideFieldsState = {
  status: 'loading' | 'ready' | 'error';
  fields: EditableField[];
  lists: ListGroup[];
  layout: SlideLayout | null;
  accent: SlideAccent | null;
  media: MediaField[];
  options: SlideOption[];
  sourceHash: string | null;
  error: string | null;
};

/** Where a media swap's new bytes come from — an upload, an RKH bank pick, or
 *  a file already in this deck's assets/. */
export type MediaPayload =
  | { file: Blob; filename: string; assetName?: never; bankId?: never }
  | { assetName: string; file?: never; filename?: never; bankId?: never }
  | { bankId: string; file?: never; filename?: never; assetName?: never };

/** One conflict, built the same way at all eight sites that raise one. */
const conflictFrom = (err: unknown, retry: FieldEdit | null = null): SlideConflict => ({
  message: errorMessage(err),
  code: errorCode(err),
  retry,
});

/**
 * The Edit tab's data layer: fetch a slide's editable fields + accent
 * (GET /api/slides/:slug/file), save text edits field-by-field on blur
 * (PUT, serialised, carrying the rolling sourceHash so concurrent writers
 * are detected), and change the accent (PATCH /slide/accent).
 *
 * 409s surface as `conflict` ({ message, code, retry }). Two kinds, told
 * apart by `code`:
 *   - 'stale'   — the file changed on disk (chat Apply, another tab). reload()
 *                 is safe; overwrite() re-reads the hash and re-applies the
 *                 pending edit on top.
 *   - 'preview' — a chat proposal is being previewed on this slide; the file
 *                 is the proposal, not the original. Overwriting just loops
 *                 (the preview re-blocks), so `retry` is null — discardPreview()
 *                 reverts the proposal and unblocks editing.
 */
export function useSlideFields(
  slug: string,
  slideId: string,
  { onSaved }: { onSaved?: (res: SlideWritePayload) => void } = {},
) {
  const [state, setState] = useState<SlideFieldsState>({
    status: 'loading',
    fields: [],
    lists: [],
    layout: null,
    accent: null,
    media: [],
    options: [],
    sourceHash: null,
    error: null,
  });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<SlideConflict | null>(null);
  // Serialise PUTs: each save chains on the last so the rolling hash is right.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const hashRef = useRef<string | null>(null);
  const aliveRef = useRef(true);

  const fileUrl = `/api/slides/${slug}/file?id=${encodeURIComponent(slideId)}`;

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    setConflict(null);
    try {
      const data = await api.get<SlideFilePayload>(fileUrl);
      hashRef.current = data.sourceHash;
      if (!aliveRef.current) return;
      setState({
        status: 'ready',
        fields: data.fields || [],
        lists: data.lists || [],
        layout: data.layout || null,
        accent: data.accent || null,
        media: data.media || [],
        options: data.options || [],
        sourceHash: data.sourceHash,
        error: null,
      });
    } catch (err) {
      if (!aliveRef.current) return;
      setState({
        status: 'error',
        fields: [],
        lists: [],
        layout: null,
        accent: null,
        media: [],
        options: [],
        sourceHash: null,
        error: errorMessage(err),
      });
    }
  }, [fileUrl]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const saveField = useCallback(
    (fieldId: string, value: string) => {
      const edit = { id: fieldId, value };
      queueRef.current = queueRef.current.then(async () => {
        if (!aliveRef.current) return;
        setSaving(true);
        try {
          const res = await api.put<SlideWritePayload>(fileUrl, { edits: [edit], ifMatch: hashRef.current });
          hashRef.current = res.sourceHash;
          if (!aliveRef.current) return;
          setState((s) => ({
            ...s,
            fields: res.fields || s.fields,
            lists: res.lists || s.lists,
            sourceHash: res.sourceHash,
          }));
          setConflict(null);
          onSaved?.(res);
        } catch (err) {
          if (!aliveRef.current) return;
          // A preview conflict can't be overwritten (the proposal re-blocks the
          // next write) — only a stale-hash conflict offers a re-apply.
          const retry = err instanceof ApiError && err.status === 409 && err.code !== 'preview' ? edit : null;
          setConflict(conflictFrom(err, retry));
        } finally {
          if (aliveRef.current) setSaving(false);
        }
      });
      return queueRef.current;
    },
    [fileUrl, onSaved],
  );

  /** Re-read the slide and re-apply the conflicted edit on top. */
  const overwrite = useCallback(async () => {
    const retry = conflict?.retry;
    if (!retry) return;
    try {
      const data = await api.get<SlideFilePayload>(fileUrl);
      hashRef.current = data.sourceHash;
      setConflict(null);
      await saveField(retry.id, retry.value);
    } catch (err) {
      setConflict(conflictFrom(err));
    }
  }, [conflict, fileUrl, saveField]);

  const setAccent = useCallback(
    async (color: string) => {
      setSaving(true);
      try {
        const res = await api.patch<SlideWritePayload>(
          `/api/slides/${slug}/slide/accent?id=${encodeURIComponent(slideId)}`,
          { color },
        );
        hashRef.current = res.sourceHash;
        if (!aliveRef.current) return res;
        setState((s) => ({ ...s, accent: res.accent || s.accent, sourceHash: res.sourceHash }));
        if ((res.replaced ?? 0) > 0) onSaved?.(res);
        return res;
      } catch (err) {
        if (aliveRef.current) setConflict(conflictFrom(err));
        return null;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [slug, slideId, onSaved],
  );

  /** Add, remove or move one entry of a slide list (agenda sections, cards,
   *  chart data points, table rows…). Joins the SAME promise chain as
   *  saveField — an in-flight blur-save and a structural op must serialise,
   *  or the rolling hash forks and field ids go stale mid-air. The response
   *  is the full refreshed payload (every id shifts after a CRUD op).
   *  args: { index } for add/remove, { from, to } for move. */
  const applyListOp = useCallback(
    (listId: string, op: string, args: Record<string, number> = {}) => {
      const run = queueRef.current.then(async () => {
        if (!aliveRef.current) return null;
        setSaving(true);
        try {
          const res = await api.post<SlideWritePayload>(
            `/api/slides/${slug}/slide/list?id=${encodeURIComponent(slideId)}`,
            {
              listId,
              op,
              ...args,
              ifMatch: hashRef.current,
            },
          );
          hashRef.current = res.sourceHash;
          if (!aliveRef.current) return res;
          setState((s) => ({
            ...s,
            fields: res.fields || s.fields,
            lists: res.lists || s.lists,
            layout: res.layout !== undefined ? res.layout : s.layout,
            media: res.media || s.media,
            options: res.options || s.options,
            sourceHash: res.sourceHash,
          }));
          setConflict(null);
          onSaved?.(res);
          return res;
        } catch (err) {
          if (!aliveRef.current) return null;
          // Structural ops are never blind-retried: reload is the safe answer.
          setConflict(conflictFrom(err));
          throw err;
        } finally {
          if (aliveRef.current) setSaving(false);
        }
      });
      // Keep the chain alive even when this op rejects.
      queueRef.current = run.catch(() => {});
      return run;
    },
    [slug, slideId, onSaved],
  );

  /** Switch a template slide between its layout presets (PATCH
   *  /slide/layout). Joins the SAME promise chain as saveField/applyListOp —
   *  a layout flip is a multi-splice write with a full payload refresh, so
   *  it must serialise with in-flight blur saves. */
  const setLayoutVariant = useCallback(
    (variantName: string) => {
      const run = queueRef.current.then(async () => {
        if (!aliveRef.current) return null;
        setSaving(true);
        try {
          const res = await api.patch<SlideWritePayload>(
            `/api/slides/${slug}/slide/layout?id=${encodeURIComponent(slideId)}`,
            {
              variant: variantName,
              ifMatch: hashRef.current,
            },
          );
          hashRef.current = res.sourceHash;
          if (!aliveRef.current) return res;
          setState((s) => ({
            ...s,
            fields: res.fields || s.fields,
            lists: res.lists || s.lists,
            layout: res.layout !== undefined ? res.layout : s.layout,
            media: res.media || s.media,
            options: res.options || s.options,
            sourceHash: res.sourceHash,
          }));
          setConflict(null);
          if ((res.changed ?? 0) > 0) onSaved?.(res);
          return res;
        } catch (err) {
          if (!aliveRef.current) return null;
          setConflict(conflictFrom(err));
          throw err;
        } finally {
          if (aliveRef.current) setSaving(false);
        }
      });
      queueRef.current = run.catch(() => {});
      return run;
    },
    [slug, slideId, onSaved],
  );

  /** Set one visual option (mono, scrim, look, size, columns…) — typed and
   *  validated server-side; the response carries the refreshed options. */
  const setOption = useCallback(
    async (optionId: string, value: unknown) => {
      setSaving(true);
      try {
        const res = await api.patch<SlideWritePayload>(
          `/api/slides/${slug}/slide/option?id=${encodeURIComponent(slideId)}`,
          {
            optionId,
            value,
          },
        );
        hashRef.current = res.sourceHash;
        if (!aliveRef.current) return res;
        setState((s) => ({ ...s, options: res.options || s.options, sourceHash: res.sourceHash }));
        onSaved?.(res);
        return res;
      } catch (err) {
        if (aliveRef.current) setConflict(conflictFrom(err));
        return null;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [slug, slideId, onSaved],
  );

  /** Point a media field at new media: { file, filename } (an upload, the
   *  blob already client-encoded), { bankId } (an RKH bank pick) or
   *  { assetName } (a file already in this deck's assets/). The server
   *  writes the asset + rewrites the slide's src deterministically.
   *  Throws on failure (the picker modal shows the message inline). */
  const setMedia = useCallback(
    async (fieldId: string, payload: MediaPayload) => {
      setSaving(true);
      try {
        const url = `/api/slides/${slug}/slide/set-media?id=${encodeURIComponent(slideId)}`;
        let res: SlideWritePayload;
        if (payload.file) {
          const form = new FormData();
          // `filename` is required on this arm: the encoder hands back a Blob,
          // which carries no name of its own.
          form.append('file', payload.file, payload.filename);
          form.append('fieldId', fieldId);
          res = await api.postForm<SlideWritePayload>(url, form);
        } else if (payload.assetName) {
          res = await api.post<SlideWritePayload>(url, { fieldId, assetName: payload.assetName });
        } else {
          res = await api.post<SlideWritePayload>(url, { fieldId, bankId: payload.bankId });
        }
        hashRef.current = res.sourceHash;
        await load(); // refresh the media list (and any fields the swap touched)
        onSaved?.(res);
        return res;
      } catch (err) {
        // Surface a preview/stale conflict in the panel banner (the picker also
        // shows the inline message); other errors just propagate to the picker.
        if (aliveRef.current && err instanceof ApiError && err.status === 409) {
          setConflict(conflictFrom(err));
        }
        throw err;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [slug, slideId, load, onSaved],
  );

  /** Resolve a preview conflict from the panel: revert the chat proposal that
   *  swapped this slide's file (the card stays in the thread, re-appliable),
   *  then reload the now-original fields so editing is unblocked. */
  const discardPreview = useCallback(async () => {
    try {
      await api.post(`/api/chat/${slug}/_slide/${encodeURIComponent(slideId)}/cancel-preview`);
    } catch {
      /* no preview active / already gone — reload anyway */
    }
    await load();
  }, [slug, slideId, load]);

  return {
    ...state,
    saving,
    conflict,
    reload: load,
    overwrite,
    discardPreview,
    saveField,
    setAccent,
    setMedia,
    setOption,
    applyListOp,
    setLayoutVariant,
  };
}
